import { useState, useEffect, useRef } from "react";
import { FaBell } from "react-icons/fa";
import { FaRegBell } from "react-icons/fa";
import { useU2000NotificationContext } from "../../context/notification/U2000NotificationContext";
import U2000NotificationPanel from "./U2000NotificationPanel";

/**
 * Composant icône de notification avec deux états : cloche vide (pas de notification) ou pleine (au moins une notification)
 * Badge affichant le nombre de notifications non lues
 * Style Material Design avec animation lors de nouvelles notifications
 */
const U2000NotificationIcon = () => {
  const { notifications, unreadCount, refresh } = useU2000NotificationContext();
  const [isPanelVisible, setIsPanelVisible] = useState<boolean>(false);
  const [hasNewNotification, setHasNewNotification] = useState<boolean>(false);
  const prevUnreadCountRef = useRef<number>(0);

  // Déterminer si on a des notifications (lues ou non lues)
  const hasNotifications = notifications.length > 0;

  // Animation lors de nouvelles notifications
  useEffect(() => {
    if (unreadCount > prevUnreadCountRef.current) {
      setHasNewNotification(true);
      const timer = setTimeout(() => {
        setHasNewNotification(false);
      }, 1000); // Animation pendant 1 seconde
      prevUnreadCountRef.current = unreadCount;
      return () => clearTimeout(timer);
    }
    prevUnreadCountRef.current = unreadCount;
  }, [unreadCount]);

  const togglePanel = () => {
    const willOpen = !isPanelVisible;
    setIsPanelVisible(willOpen);
    // BL-0435 : resync liste + badge à l'ouverture du panneau
    if (willOpen) {
      void refresh();
    }
  };

  const closePanel = () => {
    setIsPanelVisible(false);
  };

  return (
    <div className="relative">
      <button
        onClick={togglePanel}
        className={`relative p-2 rounded-lg transition-all duration-200 ${
          isPanelVisible ? "bg-blue-50 text-blue-600" : "hover:bg-gray-50 text-gray-600"
        } ${hasNewNotification ? "animate-pulse" : ""}`}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} non lues)` : ""}`}
      >
        {hasNotifications ? (
          <FaBell className="w-5 h-5" />
        ) : (
          <FaRegBell className="w-5 h-5" />
        )}
        {unreadCount > 0 && (
          <span
            className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-xs font-bold text-white bg-red-500 rounded-full ${
              hasNewNotification ? "scale-125" : ""
            } transition-transform duration-200`}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isPanelVisible && <U2000NotificationPanel onClose={closePanel} />}
    </div>
  );
};

export default U2000NotificationIcon;
